

import React, { useState, useEffect } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useAuth } from '../../contexts/AuthProvider';
// Jika ingin gunakan helper:
// import { getPersonnelQualifications, createPersonnelQualification, updatePersonnelQualification, deletePersonnelQualification } from '../../api/personnelQualificationApi';

const isRowActive = (row) => row?.isActive === undefined || row?.isActive === null || row.isActive === true;

const PersonnelQualification = () => {
  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canPersonnelQualification = (key) => can(key, 'personnelQualification');

  // Form and list state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qualificationGroup, setQualificationGroup] = useState('');
  const [qualificationName, setQualificationName] = useState('');
  // Default Qualification Level is 0 (no leveling)
  const [qualificationLevel, setQualificationLevel] = useState('0');
  const [auditorMandays, setAuditorMandays] = useState(0);
  const [qualificationNameFocusSignal, setQualificationNameFocusSignal] = useState(0);
  const [qualificationGroupFocusSignal, setQualificationGroupFocusSignal] = useState(0);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateNotes, setDeactivateNotes] = useState('');
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [levelShiftConfirmation, setLevelShiftConfirmation] = useState(null);
  const [levelShiftLoading, setLevelShiftLoading] = useState(false);
  
  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [mandaysFilter, setMandaysFilter] = useState('');
  const [qualificationGroupFilter, setQualificationGroupFilter] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  
  // User mapping state
  const [userMappings, setUserMappings] = useState([]);
  const [userMappingsLoading, setUserMappingsLoading] = useState(false);
  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [mappingQualification, setMappingQualification] = useState(null);
  // Pagination & search for user mapping modal
  const [userMappingPage, setUserMappingPage] = useState(1);
  const [userMappingPageSize, setUserMappingPageSize] = useState(10);
  const [userMappingSearch, setUserMappingSearch] = useState('');

  const [qualificationGroupsLoadedFromApi, setQualificationGroupsLoadedFromApi] = useState(false);
  const CANONICAL_QUALIFICATION_GROUP_OPTIONS = [
    { value: 'Auditor', label: 'Auditor' },
    { value: 'Non Auditor', label: 'Non Auditor' }
  ];

  const DEFAULT_QUALIFICATION_GROUP_OPTIONS = [
    { value: 'Technical Expert', label: 'Technical Expert' },
    { value: 'Lead Validator / Verifier', label: 'Lead Validator / Verifier' },
    { value: 'Validator / Verifier', label: 'Validator / Verifier' },
    { value: 'Application Reviewer', label: 'Application Reviewer' },
    { value: 'Scheduler / Operation', label: 'Scheduler / Operation' },
    { value: 'Technical Reviewer', label: 'Technical Reviewer' },
    { value: 'Certification Authority', label: 'Certification Authority' },
    { value: 'Certification Reviewer', label: 'Certification Reviewer' },
    { value: 'Mass Balance Reviewer', label: 'Mass Balance Reviewer' },
    { value: 'GHG Expert', label: 'GHG Expert' },
    { value: 'Independent Reviewer', label: 'Independent Reviewer' }
  ];

  const [qualificationGroupOptions, setQualificationGroupOptions] = useState([
    ...CANONICAL_QUALIFICATION_GROUP_OPTIONS,
    ...DEFAULT_QUALIFICATION_GROUP_OPTIONS
  ]);
  const [qualificationOptionsByGroup, setQualificationOptionsByGroup] = useState({}); // { [groupLower]: {value,label}[] }
  const [qualificationOptionsLoadedFromApiByGroup, setQualificationOptionsLoadedFromApiByGroup] = useState({}); // { [groupLower]: boolean }

  const normalizeGroupKey = (group) => String(group || '').trim().toLowerCase();
  const normalizeNameKey = (value) => String(value || '').trim().toLowerCase();
  const isCanonicalQualificationGroup = (value) => ['auditor', 'non auditor'].includes(normalizeGroupKey(value));
  const extractItems = (payload) => {
    if (Array.isArray(payload)) return payload;
    return payload?.content || payload?.items || payload?.data || [];
  };

  const AUDITOR_ROLE_NAME_OPTIONS = [
    { value: 'Principal Auditor', label: 'Principal Auditor' },
    { value: 'Lead Auditor', label: 'Lead Auditor' },
    { value: 'Lead Auditor in Trainee', label: 'Lead Auditor in Trainee' },
    { value: 'Auditor', label: 'Auditor' },
    { value: 'Auditor in Trainee', label: 'Auditor in Trainee' },
    { value: 'Observer', label: 'Observer' }
  ];

  const getQualificationOptionsForGroup = (rg) => {
    const group = String(rg || '').trim();
    if (!group) return [];

    const key = group.toLowerCase();
    const loadedFromApi = Boolean(qualificationOptionsLoadedFromApiByGroup[key]);
    const cached = Array.isArray(qualificationOptionsByGroup[key]) ? qualificationOptionsByGroup[key] : [];

    // If we have API-backed options for this group, use them as source of truth.
    if (loadedFromApi) {
      return cached;
    }

    const fromData = Array.from(new Set(
      rows
        .map(r => deriveSelectionFromRow(r))
        .filter(d => String(d.qualificationGroup || '').trim().toLowerCase() === key)
        .map(d => String(d.qualificationName || '').trim())
        .filter(Boolean)
    )).map(v => ({ value: v, label: v }));

    const merged = [...cached];
    fromData.forEach((opt) => {
      if (!merged.some(o => String(o.value).toLowerCase() === String(opt.value).toLowerCase())) {
        merged.push(opt);
      }
    });

    if (key === 'auditor') {
      // Seed common auditor role names
      AUDITOR_ROLE_NAME_OPTIONS.slice().reverse().forEach((opt) => {
        if (!merged.some(o => String(o.value).toLowerCase() === String(opt.value).toLowerCase())) {
          merged.unshift(opt);
        }
      });
    }

    return merged;
  };

  const normalizeGroupNames = (data) => {
    if (!Array.isArray(data)) return [];
    return data
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.name || item.qualificationGroup || item.group || item.value || item.label;
        }
        return '';
      })
      .map(v => String(v || '').trim())
      .filter(Boolean);
  };

  const mergeUniqueOptions = (primary, secondary) => {
    const out = [];
    const seen = new Set();
    const add = (opt) => {
      const v = String(opt?.value || '').trim();
      if (!v) return;
      const key = v.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ value: v, label: String(opt?.label || v) });
    };
    (Array.isArray(primary) ? primary : []).forEach(add);
    (Array.isArray(secondary) ? secondary : []).forEach(add);
    return out;
  };

  const fetchQualificationGroupsFromApi = async () => {
    // Backend supports both plural & singular paths.
    const endpoints = [
      '/audit/personnel-qualifications/qualification-groups',
      '/audit/personnel-qualifications/qualification-group',
      // Legacy fallback
      '/audit/personnel-qualifications/roles'
    ];

    for (const url of endpoints) {
      try {
        const res = await api.get(url);
        let data = res.data;
        
        // Handle array of objects with 'id' and 'name' properties (from new API)
        if (Array.isArray(data) && data.length > 0 && data[0]?.name) {
          const apiOptions = data.map(item => ({ 
            value: String(item.name || '').trim(), 
            label: String(item.name || '').trim() 
          }));
          setQualificationGroupOptions(mergeUniqueOptions(CANONICAL_QUALIFICATION_GROUP_OPTIONS, apiOptions));
          setQualificationGroupsLoadedFromApi(true);
          console.log('✅ Loaded qualification groups from API:', apiOptions);
          return;
        }
        
        // Handle array of strings (legacy API)
        const names = normalizeGroupNames(data);
        if (!names.length) continue;

        const apiOptions = names.map(v => ({ value: v, label: v }));
        setQualificationGroupOptions(mergeUniqueOptions(CANONICAL_QUALIFICATION_GROUP_OPTIONS, apiOptions));
        setQualificationGroupsLoadedFromApi(true);
        console.log('✅ Loaded qualification groups from API:', apiOptions);
        return;
      } catch (e) {
        console.warn(`Failed to fetch from ${url}:`, e?.response?.status);
        // try next endpoint
      }
    }

    // If API failed/empty, keep fallbacks (defaults + hydrated-from-data).
    console.warn('⚠️ Failed to load qualification groups from API, using defaults');
    setQualificationGroupsLoadedFromApi(false);
  };

  const fetchQualificationsForGroupFromApi = async (rg) => {
    const group = String(rg || '').trim();
    if (!group) return;

    const key = group.toLowerCase();
    if (qualificationOptionsLoadedFromApiByGroup[key]) return;

    const endpoints = [
      `/audit/personnel-qualifications/qualification-group/${encodeURIComponent(group)}/qualification`,
      `/audit/personnel-qualifications/qualification-groups/${encodeURIComponent(group)}/qualifications`,
      // legacy endpoint
    ];

    for (const url of endpoints) {
      try {
        const res = await api.get(url);
        let data = res.data;
        
        // Handle array of strings or option-like objects
        if (Array.isArray(data)) {
          const cleaned = data
            .map((item) => {
              if (typeof item === 'string') return item;
              if (item && typeof item === 'object') {
                return item.name || item.qualification || item.label || item.value;
              }
              return '';
            })
            .map(v => String(v || '').trim())
            .filter(Boolean)
            .map(v => ({ value: v, label: v }));
          
          setQualificationOptionsByGroup(prev => ({ ...prev, [key]: cleaned }));
          setQualificationOptionsLoadedFromApiByGroup(prev => ({ ...prev, [key]: true }));
          console.log(`✅ Loaded qualifications for group "${group}":`, cleaned);
          return;
        }
      } catch (e) {
        console.warn(`Failed to fetch qualifications for group "${group}" from ${url}:`, e?.response?.status);
        // try next endpoint
      }
    }

    console.warn(`⚠️ Failed to load qualifications for group "${group}" from API`);
    setQualificationOptionsLoadedFromApiByGroup(prev => ({ ...prev, [key]: false }));
    // Keep existing options if any (manual/derived), otherwise set empty.
    setQualificationOptionsByGroup(prev => {
      if (Array.isArray(prev[key]) && prev[key].length) return prev;
      return { ...prev, [key]: [] };
    });
  };

  const deriveSelectionFromRow = (row) => {
    const rowGroup = String(row?.qualificationGroup || row?.roleGroup || '').trim();
    const rowName = String(row?.qualification || row?.qualificationName || row?.roleName || '').trim();
    const rowLevel = row?.qualificationLevel ?? row?.roleOrder ?? row?.qualificationLevel;

    if (rowGroup && rowName) {
      return {
        qualificationGroup: rowGroup,
        qualificationName: rowName,
        qualificationLevel: rowLevel === null || rowLevel === undefined ? 0 : Number(rowLevel)
      };
    }

    const q = String(row?.qualification || '').trim();

    // New format: "Group - RoleName (Level N)" or "Group - RoleName"
    const dash = q.match(/^(.+?)\s*-\s*(.+?)(?:\s*\(\s*level\s*(\d+)\s*\)\s*)?$/i);
    if (dash) {
      return {
        qualificationGroup: String(dash[1] || '').trim(),
        qualificationName: String(dash[2] || '').trim(),
        qualificationLevel: rowLevel === null || rowLevel === undefined ? 0 : Number(rowLevel)
      };
    }

    // Legacy Auditor label rows
    const auditorMatch = AUDITOR_ROLE_NAME_OPTIONS.find(o => o.label.toLowerCase() === q.toLowerCase());
    if (auditorMatch) {
      return { qualificationGroup: 'Auditor', qualificationName: auditorMatch.label, qualificationLevel: 0 };
    }

    // Legacy format: "Role (Level N)" -> group=roleName=Role
    const m = q.match(/^(.+?)\s*\(\s*level\s*(\d+)\s*\)\s*$/i);
    if (m) {
      const v = String(m[1] || '').trim();
      return { qualificationGroup: v, qualificationName: v, qualificationLevel: 0 };
    }

    // Legacy plain qualification: treat as group+name
    return { qualificationGroup: q, qualificationName: q, qualificationLevel: 0 };
  };

  const getRowGroupNameLevel = (row) => {
    const derived = deriveSelectionFromRow(row);
    const group = String(derived?.qualificationGroup || '').trim();
    const name = String(derived?.qualificationName || '').trim();
    const levelRaw = derived?.qualificationLevel;
    const level = levelRaw === null || levelRaw === undefined || Number.isNaN(Number(levelRaw)) ? 0 : Number(levelRaw);
    return { group, name, level };
  };

  const buildPayloadFromForm = (id = null, formValues = null) => {
    const selectedGroup = String(formValues?.qualificationGroup ?? qualificationGroup ?? '').trim();
    const selectedName = String(formValues?.qualificationName ?? qualificationName ?? '').trim();
    const selectedLevel = formValues?.qualificationLevel ?? qualificationLevel;
    const selectedMandays = formValues?.auditorMandays ?? auditorMandays;
    let levelNum = selectedLevel !== '' && selectedLevel !== null && selectedLevel !== undefined ? Number(selectedLevel) : 0;
    if (Number.isNaN(levelNum)) levelNum = 0;

    const payload = {
      qualificationGroup: selectedGroup || null,
      qualification: selectedName || null,
      qualificationLevel: levelNum,
      auditorMandays: Number(selectedMandays) === 1 ? 1 : 0
    };

    return id ? { ...payload, id } : payload;
  };

  const getCurrentFormValues = () => ({
    qualificationGroup: String(qualificationGroup || '').trim(),
    qualificationName: String(qualificationName || '').trim(),
    qualificationLevel,
    auditorMandays: Number(auditorMandays) === 1 ? 1 : 0
  });

  const getDisplayQualificationGroup = (row) => {
    if (row?.qualificationGroup) return row.qualificationGroup;
    const derived = deriveSelectionFromRow(row);
    return derived.qualificationGroup || row?.qualification || '-';
  };

  const getDisplayQualificationName = (row) => {
    if (row?.qualification) return row.qualification;
    const derived = deriveSelectionFromRow(row);
    return derived.qualificationName || '-';
  };

  const getDisplayQualificationLevel = (row) => {
    if (row?.qualificationLevel !== null && row?.qualificationLevel !== undefined) return row.qualificationLevel;
    const derived = deriveSelectionFromRow(row);
    return derived.qualificationLevel ?? 0;
  };

  const fetchQualifications = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      // Bisa pakai helper: const data = await getPersonnelQualifications();
      const res = await api.get('/audit/personnel-qualifications');
      const qualificationItems = extractItems(res.data);
      if (!qualificationItems.length) {
        setRows([]);
        return [];
      }
      
      // Initialize with default count
      const initialRows = qualificationItems.map(qual => ({
        ...qual,
        userMappingCount: 0
      }));
      setRows(initialRows);
      
      // Fetch user counts in parallel
      const counts = await Promise.all(
        initialRows.map(async (qual) => {
          try {
            const userRes = await api.get(`/audit/personnel-qualifications/${qual.id}/users`);
            const userItems = extractItems(userRes.data);
            return {
              qualificationId: qual.id,
              count: userItems.length
            };
          } catch (err) {
            console.warn(`Warning: Could not fetch users for qualification ${qual.id}:`, err);
            return { qualificationId: qual.id, count: 0 };
          }
        })
      );
      
      // Update rows with actual counts
      const updatedRows = initialRows.map(qual => {
        const countData = counts.find(c => c.qualificationId === qual.id);
        return {
          ...qual,
          userMappingCount: countData ? countData.count : 0
        };
      });
      
      setRows(updatedRows);

      // Hydrate Role options from existing data (including legacy rows)
      // Only hydrate from rows if we couldn't load authoritative groups from API.
      if (!qualificationGroupsLoadedFromApi) {
        const rolesFromData = Array.from(new Set(
          updatedRows
            .map(r => deriveSelectionFromRow(r).qualificationGroup)
            .map(v => String(v || '').trim())
            .filter(Boolean)
        ));
        if (rolesFromData.length) {
          const fromDataOptions = rolesFromData.map(v => ({ value: v, label: v }));
          setQualificationGroupOptions(prev => mergeUniqueOptions(fromDataOptions, prev));
        }
      }
      return updatedRows;
    } catch (err) {
      console.error('Failed to fetch qualifications:', err);
      setRows([]);
      return [];
    }
    finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const doesQualificationMatchPayload = (row, payload) => {
    const derived = getRowGroupNameLevel(row);
    return (
      normalizeGroupKey(derived.group) === normalizeGroupKey(payload.qualificationGroup) &&
      normalizeNameKey(derived.name) === normalizeNameKey(payload.qualification) &&
      Number(derived.level ?? 0) === Number(payload.qualificationLevel ?? 0) &&
      Number(row?.auditorMandays ?? 0) === Number(payload.auditorMandays ?? 0)
    );
  };

  const reconcileQualificationMutation = async ({ mode, payload, id }) => {
    const refreshedRows = await fetchQualifications({ silent: true });

    if (mode === 'create') {
      return refreshedRows.some((row) => doesQualificationMatchPayload(row, payload));
    }

    if (mode === 'update') {
      const matchedRow = refreshedRows.find((row) => String(row.id) === String(id));
      return matchedRow ? doesQualificationMatchPayload(matchedRow, payload) : false;
    }

    return false;
  };

  const fetchUserMappings = async (qualificationId) => {
    setUserMappingsLoading(true);
    try {
      const res = await api.get(`/audit/personnel-qualifications/${qualificationId}/users`);
      setUserMappings(extractItems(res.data));
      setUserMappingPage(1); // Reset page on open
      setUserMappingSearch(''); // Reset search on open
    } catch (err) {
      console.error('Failed to fetch user mappings:', err);
      setUserMappings([]);
    }
    setUserMappingsLoading(false);
  };

  useEffect(() => {
    fetchQualifications();
    fetchQualificationGroupsFromApi();
  }, []);

  const handleAdd = () => {
    if (!canPersonnelQualification('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    setEditing(null);
    setQualificationGroup('');
    setQualificationName('');
    setQualificationLevel('0'); // Default to 0 (no leveling)
    setAuditorMandays(0);
    setLevelShiftConfirmation(null);
    fetchQualificationGroupsFromApi();
    setQualificationGroupFocusSignal(s => s + 1);
    setModalVisible(true);
  };

  const ensureQualificationGroupOption = (val) => {
    const v = String(val || '').trim();
    if (!v) return;
    setQualificationGroupOptions(prev => {
      if (prev.some(o => String(o.value).toLowerCase() === v.toLowerCase())) return prev;
      return [...prev, { value: v, label: v }];
    });
  };

  const ensureQualificationOption = (rg, val) => {
    const group = String(rg || '').trim();
    const v = String(val || '').trim();
    if (!group || !v) return;
    const key = group.toLowerCase();
    setQualificationOptionsByGroup(prev => {
      const next = { ...prev };
      const existing = Array.isArray(next[key]) ? [...next[key]] : [];
      if (!existing.some(o => String(o.value).toLowerCase() === v.toLowerCase())) {
        existing.push({ value: v, label: v });
      }
      next[key] = existing;
      return next;
    });
  };

  const handleQualificationGroupChange = (_name, val) => {
    const v = String(val || '').trim();
    if (v) ensureQualificationGroupOption(v);
    setQualificationGroup(v);
    // Reset qualification when changing group
    setQualificationName('');
    if (v) fetchQualificationsForGroupFromApi(v);
  };

  const handleQualificationNameChange = (_name, val) => {
    const v = String(val || '').trim();
    if (v) ensureQualificationOption(qualificationGroup, v);
    setQualificationName(v);
  };

  const handleViewUsers = (row) => {
    setMappingQualification(row);
    fetchUserMappings(row.id);
    setMappingModalVisible(true);
  };

  const handleEdit = (row) => {
    if (!canPersonnelQualification('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    setEditing(row);
    setLevelShiftConfirmation(null);
    const derived = deriveSelectionFromRow(row);
    fetchQualificationGroupsFromApi();
    ensureQualificationGroupOption(derived.qualificationGroup);
    setQualificationGroup(derived.qualificationGroup);
    ensureQualificationOption(derived.qualificationGroup, derived.qualificationName);
    setQualificationName(derived.qualificationName);
    setQualificationLevel(
      derived.qualificationLevel !== null && derived.qualificationLevel !== undefined
        ? String(derived.qualificationLevel)
        : '0'
    );
    setAuditorMandays(row.auditorMandays);
    setQualificationGroupFocusSignal(s => s + 1);
    setModalVisible(true);
    if (derived.qualificationGroup) fetchQualificationsForGroupFromApi(derived.qualificationGroup);
  };

  const openDeactivateModal = (row) => {
    if (!canPersonnelQualification('edit')) { setFailMessage('You do not have permission to change this status.'); setShowFailModal(true); return; }
    setDeactivateTarget(row);
    setDeactivateNotes('');
    setDeactivateError('');
  };

  const closeDeactivateModal = () => {
    setDeactivateTarget(null);
    setDeactivateNotes('');
    setDeactivateError('');
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    if (!deactivateNotes.trim()) {
      setDeactivateError('Notes are required to deactivate a qualification.');
      return;
    }
    setDeactivateLoading(true);
    try {
      await api.patch(`/audit/personnel-qualifications/${deactivateTarget.id}/status`, {
        active: false,
        notes: deactivateNotes.trim()
      });
      closeDeactivateModal();
      setSuccessMessage('Qualification deactivated successfully.');
      setShowSuccessModal(true);
      await fetchQualifications();
    } catch (err) {
      console.error('Failed to deactivate qualification:', err);
      setDeactivateError(err?.response?.data?.message || 'Failed to deactivate qualification.');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleActivate = async (row) => {
    if (!canPersonnelQualification('edit')) { setFailMessage('You do not have permission to change this status.'); setShowFailModal(true); return; }
    try {
      await api.patch(`/audit/personnel-qualifications/${row.id}/status`, { active: true });
      setSuccessMessage('Qualification activated successfully.');
      setShowSuccessModal(true);
      await fetchQualifications();
    } catch (err) {
      console.error('Failed to activate qualification:', err);
      setFailMessage(err?.response?.data?.message || 'Failed to activate qualification.');
      setShowFailModal(true);
    }
  };

  const updateQualificationRecord = async (qualificationId, payload) => {
    try {
      await api.put(`/audit/personnel-qualifications/${qualificationId}`, payload);
    } catch (updateError) {
      if (![404, 405].includes(updateError?.response?.status)) {
        throw updateError;
      }
      await api.post('/audit/personnel-qualifications', payload);
    }
  };

  const submitQualification = async ({ formValues = null, confirmedShiftPlan = null } = {}) => {
    const activeFormValues = formValues || getCurrentFormValues();

    if (!activeFormValues.qualificationGroup) {
      setFailMessage('Qualification Group is required.');
      setShowFailModal(true);
      return;
    }
    if (!activeFormValues.qualificationName) {
      setFailMessage('Qualification is required.');
      setShowFailModal(true);
      return;
    }

    const selectedGroup = String(activeFormValues.qualificationGroup || '').trim();
    const selectedLevel = activeFormValues.qualificationLevel ? Number(activeFormValues.qualificationLevel) : null;
    const selectedName = String(activeFormValues.qualificationName || '').trim();
    const selectedNameKey = normalizeNameKey(selectedName);
    const currentId = editing?.id;
    const isEditing = Boolean(editing);

    if (!isEditing && !isCanonicalQualificationGroup(selectedGroup)) {
      setFailMessage('Qualification Group for new records must be either "Auditor" or "Non Auditor".');
      setShowFailModal(true);
      setQualificationGroupFocusSignal(s => s + 1);
      return;
    }

    // If another qualification in the same group already uses this level,
    // warn user and auto-shift existing levels down (L -> L+1, etc.) before saving.
    const groupKey = normalizeGroupKey(selectedGroup);
    const sameGroupRows = rows
      .map(r => ({ row: r, d: getRowGroupNameLevel(r) }))
      .filter(x => normalizeGroupKey(x.d.group) === groupKey)
      .filter(x => x.row?.id !== currentId);

    // Backend enforces unique (group, qualification). Block duplicates early with a clear message.
    const duplicateNameRows = sameGroupRows.filter(x => normalizeNameKey(x.d.name) === selectedNameKey);
    if (duplicateNameRows.length > 0) {
      setFailMessage(
        `Qualification "${selectedName}" already exists in Qualification Group "${selectedGroup}". ` +
        `Please edit the existing record instead of creating another one.`
      );
      setShowFailModal(true);
      setQualificationNameFocusSignal(s => s + 1);
      return;
    }

    const hasRankedLevel = Number.isFinite(selectedLevel) && selectedLevel > 0;
    const conflictsAtSameLevel = hasRankedLevel
      ? sameGroupRows.filter(x => Number(x.d.level) === selectedLevel)
      : [];

    const original = isEditing ? getRowGroupNameLevel(editing) : null;
    const originalGroupKey = normalizeGroupKey(original?.group);
    const originalNameKey = String(original?.name || '').trim().toLowerCase();
    const originalLevel = original?.level ?? 0;
    const isSameGroupAndQualificationAsOriginal =
      isEditing && groupKey === originalGroupKey && selectedNameKey === originalNameKey;
    const isIncreasingLevel = !isEditing ? true : selectedLevel > Number(originalLevel);

    // If editing and changing group and/or qualification name, treat as inserting into a new lane:
    // allow shifting on any conflict (like Add), regardless of original level direction.
    const isTreatAsInsertIntoTargetGroup = !isEditing || !isSameGroupAndQualificationAsOriginal;

    const shouldAutoShift =
      hasRankedLevel &&
      (conflictsAtSameLevel.length > 0) &&
      (
        (!isEditing) ||
        (isTreatAsInsertIntoTargetGroup) ||
        (isSameGroupAndQualificationAsOriginal && isIncreasingLevel)
      );

    const toShift = shouldAutoShift
      ? sameGroupRows
        .filter(x => Number(x.d.level) >= selectedLevel)
        .sort((a, b) => {
          const dl = Number(a.d.level) - Number(b.d.level);
          if (dl !== 0) return dl;
          return Number(a.row?.id || 0) - Number(b.row?.id || 0);
        })
        .reverse()
      : [];

    if (shouldAutoShift && !confirmedShiftPlan) {
      const first = conflictsAtSameLevel[0];
      const conflictLabel = first?.d?.name || first?.row?.qualification || 'data lain';
      const more = conflictsAtSameLevel.length > 1 ? ` (+${conflictsAtSameLevel.length - 1} data lain)` : '';

      // setLevelShiftConfirmation({
      //   selectedGroup,
      //   selectedLevel,
      //   selectedName,
      //   conflictLabel,
      //   more,
      //   toShift,
      //   formValues: activeFormValues
      // });
      return false;
    }

    if (shouldAutoShift && confirmedShiftPlan) {
      const shiftTargets = Array.isArray(confirmedShiftPlan.toShift) ? confirmedShiftPlan.toShift : toShift;
      const mappedRowsInShift = shiftTargets.filter(item => Number(item.row?.userMappingCount || 0) > 0);
      if (mappedRowsInShift.length > 0) {
        const blockedNames = mappedRowsInShift.map(item => `"${item.d?.name || item.row?.qualification || `ID ${item.row?.id}`}"`).join(', ');
        setFailMessage(
          `Tidak dapat menggeser level karena beberapa kualifikasi sudah dipetakan ke user: ${blockedNames}. ` +
          `Silakan pilih Qualification Level lain yang tidak bentrok dengan data yang sedang digunakan.`
        );
        setShowFailModal(true);
        return;
      }

      // Ensure group exists in options for downstream controls
      ensureQualificationGroupOption(selectedGroup);

      const shiftErrors = [];
      for (const item of shiftTargets) {
        const row = item.row;
        const d = item.d;
        const updatePayload = {
          id: row.id,
          qualificationGroup: selectedGroup || null,
          qualification: d.name || String(row?.qualification || '').trim() || null,
          qualificationLevel: Number(d.level) + 1,
          auditorMandays: Number(row?.auditorMandays ?? 0) === 1 ? 1 : 0
        };
        try {
          await updateQualificationRecord(row.id, updatePayload);
        } catch (err) {
          console.error(`Failed to shift qualification "${d.name}" (ID ${row.id}):`, err);
          const backendMsg = err?.response?.data?.message || err?.message || 'Unknown error';
          shiftErrors.push({
            name: d.name || String(row?.qualification || '').trim() || `ID ${row.id}`,
            message: backendMsg
          });
        }
      }

      if (shiftErrors.length > 0) {
        const blockedNames = shiftErrors.map(e => `"${e.name}"`).join(', ');
        const detailMsg = shiftErrors[0]?.message;
        setFailMessage(
          `Gagal menggeser Qualification Level untuk: ${blockedNames}. ` +
          `${detailMsg ? `Detail: ${detailMsg} ` : ''}` +
          `Data tersebut sedang direferensikan oleh sistem (competency, impartiality, atau workflow). ` +
          `Silakan pilih Qualification Level lain yang tidak bentrok.`
        );
        setShowFailModal(true);
        return;
      }

      // Refresh rows after successful shifts to get current state before creating/updating
      await fetchQualifications({ silent: true });
    }

    try {
      const createPayload = buildPayloadFromForm(null, activeFormValues);
      if (editing) {
        const updatePayload = buildPayloadFromForm(editing.id, activeFormValues);
        await updateQualificationRecord(editing.id, updatePayload);
        setSuccessMessage('Qualification updated successfully.');
      } else {
        await api.post('/audit/personnel-qualifications', createPayload);
        setSuccessMessage('Qualification created successfully.');
      }
      setShowSuccessModal(true);
      setModalVisible(false);
      setLevelShiftConfirmation(null);
      await fetchQualifications();
    } catch (err) {
      console.error('Failed to save qualification:', err);
      const backendMsg = err?.response?.data?.message || '';
      const isUniqueViolation = backendMsg.toLowerCase().includes('already exists') ||
        backendMsg.toLowerCase().includes('duplicate');

      const operationApplied = await reconcileQualificationMutation({
        mode: editing ? 'update' : 'create',
        payload: editing ? buildPayloadFromForm(editing.id, activeFormValues) : buildPayloadFromForm(null, activeFormValues),
        id: editing?.id
      });

      if (operationApplied) {
        setSuccessMessage(editing ? 'Qualification updated successfully.' : 'Qualification created successfully.');
        setShowSuccessModal(true);
        setModalVisible(false);
        setLevelShiftConfirmation(null);
        return;
      }

      if (isUniqueViolation) {
        await fetchQualifications({ silent: true });
        setFailMessage(
          `A qualification with this name already exists in the "${selectedGroup}" group. ` +
          `Please use a different name or edit the existing record.`
        );
      } else {
        setFailMessage(backendMsg || 'Failed to save qualification.');
      }
      setShowFailModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitQualification();
  };

  const handleConfirmLevelShift = async () => {
    if (!levelShiftConfirmation) return;

    const confirmedPlan = levelShiftConfirmation;
    setLevelShiftConfirmation(null);
    setLevelShiftLoading(true);
    try {
      await submitQualification({
        formValues: confirmedPlan.formValues,
        confirmedShiftPlan: confirmedPlan
      });
    } finally {
      setLevelShiftLoading(false);
    }
  };

  const filteredRows = rows.filter((row) => {
    const query = searchQuery.trim().toLowerCase();
    // Filter by qualification group
    if (qualificationGroupFilter) {
      const group = String(getDisplayQualificationGroup(row) || '').toLowerCase();
      if (group !== String(qualificationGroupFilter).toLowerCase()) {
        return false;
      }
    }
    // Search by qualification/group/level
    if (query) {
      const q = String(row.qualification || '').toLowerCase();
      const g = String(getDisplayQualificationGroup(row) || '').toLowerCase();
      const n = String(getDisplayQualificationName(row) || '').toLowerCase();
      const lvl = String(getDisplayQualificationLevel(row) ?? '').toLowerCase();
      const lvlText = lvl ? `level ${lvl}` : '';
      if (!q.includes(query) && !g.includes(query) && !n.includes(query) && !lvlText.includes(query)) {
        return false;
      }
    }
    // Filter by mandays
    if (mandaysFilter !== '') {
      if (Number(row.auditorMandays) !== Number(mandaysFilter)) {
        return false;
      }
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + pageSize);

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }
    return pages;
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, mandaysFilter, qualificationGroupFilter, pageSize]);


  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Personnel Qualification</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={handleAdd}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Add Qualification
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Search and Filters */}
                <div className="row align-items-end gap-2 mb-4">
                  <div className="col">
                    <label className="form-label">Search</label>
                    <input
                      className="form-control"
                      placeholder="Search by qualification..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">Qualification Group</label>
                    <select
                      className="form-select"
                      value={qualificationGroupFilter}
                      onChange={e => {
                        setQualificationGroupFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">All</option>
                      {qualificationGroupOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label">Auditor Mandays</label>
                    <select
                      className="form-select"
                      value={mandaysFilter}
                      onChange={(e) => setMandaysFilter(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value={1}>Terhitung Mandays</option>
                      <option value={0}>Tidak Terhitung</option>
                    </select>
                  </div>
                  <div className="col-auto">
                    <button className="btn btn-outline-secondary" type="button" onClick={() => setPage(1)}>Filter</button>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-hover">
                    <thead>
                      <tr>
                        <th style={{width: '40px'}}><input type="checkbox" className="form-check-input" /></th>
                        <th>Qualification Group</th>
                        <th>Qualification</th>
                        <th>Qualification Level</th>
                        <th>Auditor Mandays</th>
                        <th>Total Competency Mapped</th>
                        <th>Created</th>
                        <th>Active</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">
                            No qualifications found
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map(row => (
                          <tr key={row.id}>
                            <td><input type="checkbox" className="form-check-input" /></td>
                            <td><strong>{getDisplayQualificationGroup(row)}</strong></td>
                            <td>{getDisplayQualificationName(row)}</td>
                            <td>
                              {Number(getDisplayQualificationLevel(row)) === 0 ? (
                                <span className="badge bg-light text-secondary border" style={{fontWeight: 500}} title="No Leveling Set">Not Set</span>
                              ) : (
                                <span className="badge bg-primary text-light" style={{fontWeight: 500}}>{getDisplayQualificationLevel(row)}</span>
                              )}
                            </td>
                            <td>{row.auditorMandays === 1 ? (
                              <span title="Terhitung Mandays">
                                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="12" fill="#198754" />
                                  <path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            ) : (
                              <span>-</span>
                            )}</td>
                            <td><button className="btn btn-outline-primary" onClick={() => handleViewUsers(row)}>{row.userMappingCount || 0}</button></td>
                            <td>{row.cdate ? new Date(row.cdate).toLocaleDateString() : '-'}</td>
                            <td>
                              {isRowActive(row) ? (
                                <span className="badge bg-success text-light">Active</span>
                              ) : (
                                <span className="badge bg-secondary text-light">Inactive</span>
                              )}
                            </td>
                            <td>
                              <div className="btn-list gap-1">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(row)}>Edit</button>
                                {isRowActive(row) ? (
                                  <button className="btn btn-sm btn-outline-warning" onClick={() => openDeactivateModal(row)}>Deactivate</button>
                                ) : (
                                  <button className="btn btn-sm btn-outline-success" onClick={() => handleActivate(row)}>Activate</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between mt-4">
                  <div>
                    <label className="form-label small mb-1">Records per page</label>
                    <select
                      className="form-select form-select-sm"
                      style={{width: '100px'}}
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm m-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          type="button"
                          aria-label="Previous"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                      </li>
                      {getPageNumbers().map((pageNumber) => (
                        <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                          <button className="page-link" type="button" onClick={() => setPage(pageNumber)}>
                            {pageNumber}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          type="button"
                          aria-label="Next"
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Qualification Modal */}
      {modalVisible && (
        <>
          <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">{editing ? 'Edit' : 'Add'} Qualification</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setModalVisible(false);
                        setLevelShiftConfirmation(null);
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                        <TomSelect
                            name="qualificationGroup"
                            value={qualificationGroup}
                            onChange={handleQualificationGroupChange}
                            options={qualificationGroupOptions}
                            placeholder="Select qualification group"
                            label="Qualification Group"
                          required={true}
                         valueField="value"
                         labelField="label"
                         searchField="label"
                         create={false}
                         focusSignal={qualificationGroupFocusSignal}
                       />
                       <small className="text-muted d-block mt-2">
                         Use <strong>Auditor</strong> or <strong>Non Auditor</strong> for new records. Legacy groups are shown only for existing data compatibility.
                       </small>
                     </div>

                       {qualificationGroup && (
                      <div className="mb-3">
                        <TomSelect
                            name="qualificationName"
                            value={qualificationName}
                            onChange={handleQualificationNameChange}
                            options={getQualificationOptionsForGroup(qualificationGroup)}
                            placeholder="Select qualification (type to add new)"
                            label="Qualification"
                          required={true}
                            valueField="value"
                            labelField="label"
                            searchField="label"
                            create={true}
                            focusSignal={qualificationNameFocusSignal}
                        />
                      </div>
                    )}

                      <div className="mb-3">
                        <label className="form-label">Qualification Level <span className="text-muted">(0 = tidak ada leveling)</span></label>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          value={qualificationLevel}
                          onChange={(e) => setQualificationLevel(e.target.value)}
                        />
                        {/* <small className="text-danger d-block mt-2">
                          <b>0</b> berarti <b>tidak memiliki leveling</b> dalam qualification ini.<br />
                          Jika ingin upgrade competency, gunakan angka 1, 2, dst. Sistem akan ambil <b>next Qualification Level</b> (rank+1) pada group yang sama.<br />
                        </small> */}
                      </div>

                    <div className="mb-3">
                      <label className="form-label">Auditor Mandays *</label>
                      <select
                        className="form-select"
                        value={auditorMandays}
                        onChange={e => setAuditorMandays(Number(e.target.value))}
                        required
                      >
                        <option value={1}>Terhitung Mandays (1)</option>
                        <option value={0}>Tidak Terhitung (0)</option>
                      </select>
                      <small className="text-muted">
                        Jika <b>Terhitung Mandays (1)</b>, maka qualification ini akan mengkonsumsi mandays saat penjadwalan audit.<br />
                        Jika <b>Tidak Terhitung (0)</b>, maka tidak mengkonsumsi mandays saat penjadwalan audit.
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => {
                        setModalVisible(false);
                        setLevelShiftConfirmation(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success">{editing ? 'Update' : 'Save'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setModalVisible(false);
              setLevelShiftConfirmation(null);
            }}
          ></div>
        </>
      )}

      {/* View User Mappings Modal */}
      {mappingModalVisible && mappingQualification && (
        <>
          <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">User Mapping - {mappingQualification.qualification}</h5>
                  <button type="button" className="btn-close" onClick={() => setMappingModalVisible(false)}></button>
                </div>
                <div className="modal-body">
                {/* Search and page size */}
                <div className="row mb-2 align-items-end">
                  <div className="col">
                    <label className="form-label">Search</label>
                    <input
                      className="form-control"
                      placeholder="Search by name, email, standard..."
                      value={userMappingSearch}
                      onChange={e => {
                        setUserMappingSearch(e.target.value);
                        setUserMappingPage(1);
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <label className="form-label small mb-1">Records per page</label>
                    <select
                      className="form-select form-select-sm"
                      style={{width: '100px'}}
                      value={userMappingPageSize}
                      onChange={e => {
                        setUserMappingPageSize(Number(e.target.value));
                        setUserMappingPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                {userMappingsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : userMappings && userMappings.length > 0 ? (
                  <>
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table table-hover">
                      <thead>
                        <tr>
                          <th style={{width: '40px'}}></th>
                          <th>Name</th>
                          <th>Standard</th>
                          <th>Sector/Scheme</th>
                          {/* <th>Request Date</th> */}
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userMappings
                          .filter(mapping => {
                            const q = userMappingSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (
                              (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                              (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                              (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                              (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                              (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                            );
                          })
                          .slice((userMappingPage-1)*userMappingPageSize, userMappingPage*userMappingPageSize)
                          .map((mapping, idx) => (
                          <tr key={idx}>
                            <td></td>
                            <td><strong>{mapping.firstName} {mapping.lastName}</strong><br /><span className="text-muted small">{mapping.email}</span></td>
                            <td><span className="badge bg-info text-white">{mapping.standard}</span></td>
                            <td><span className="text-muted small">{mapping.sectors || '-'}</span></td>
                            {/* <td>{mapping.requestDate ? new Date(mapping.requestDate).toLocaleDateString('en-GB') : '-'}</td> */}
                            <td>{(() => {
                              switch (mapping.status) {
                                case 'approved':
                                  return <span className="badge bg-success">Approved</span>;
                                case 'rejected':
                                  return <span className="badge bg-danger">Rejected</span>;
                                case 'pending':
                                  return <span className="badge bg-warning text-dark">Pending Approval</span>;
                                case 'revision_requested':
                                case 'need_revision':
                                  return <span className="badge bg-info text-dark">Need Revision</span>;
                                default:
                                  return <span className="badge bg-secondary">{mapping.status}</span>;
                              }
                            })()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <div className="small text-muted">
                      Showing {((userMappingPage-1)*userMappingPageSize)+1}
                      -{Math.min(userMappingPage*userMappingPageSize, userMappings.filter(mapping => {
                        const q = userMappingSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                          (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                          (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                          (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                          (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                        );
                      }).length)} of {userMappings.filter(mapping => {
                        const q = userMappingSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                          (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                          (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                          (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                          (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                        );
                      }).length}
                    </div>
                    <nav>
                      <ul className="pagination pagination-sm m-0">
                        <li className={`page-item ${userMappingPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            type="button"
                            aria-label="Previous"
                            onClick={() => setUserMappingPage(p => Math.max(1, p - 1))}
                            disabled={userMappingPage === 1}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                          </button>
                        </li>
                        {(() => {
                          const filtered = userMappings.filter(mapping => {
                            const q = userMappingSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (
                              (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                              (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                              (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                              (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                              (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                            );
                          });
                          const totalPages = Math.max(1, Math.ceil(filtered.length / userMappingPageSize));
                          let start = Math.max(1, userMappingPage - 2);
                          let end = Math.min(totalPages, start + 4);
                          start = Math.max(1, end - 4);
                          const pages = [];
                          for (let i = start; i <= end; i += 1) {
                            pages.push(i);
                          }
                          return pages.map(pageNumber => (
                            <li key={pageNumber} className={`page-item ${userMappingPage === pageNumber ? 'active' : ''}`}>
                              <button className="page-link" type="button" onClick={() => setUserMappingPage(pageNumber)}>
                                {pageNumber}
                              </button>
                            </li>
                          ));
                        })()}
                        <li className={`page-item ${(() => {
                          const filtered = userMappings.filter(mapping => {
                            const q = userMappingSearch.trim().toLowerCase();
                            if (!q) return true;
                            return (
                              (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                              (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                              (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                              (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                              (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                            );
                          });
                          const totalPages = Math.max(1, Math.ceil(filtered.length / userMappingPageSize));
                          return userMappingPage === totalPages ? 'disabled' : '';
                        })()}`}>
                          <button
                            className="page-link"
                            type="button"
                            aria-label="Next"
                            onClick={() => {
                              const filtered = userMappings.filter(mapping => {
                                const q = userMappingSearch.trim().toLowerCase();
                                if (!q) return true;
                                return (
                                  (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                                  (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                                  (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                                  (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                                  (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                                );
                              });
                              const totalPages = Math.max(1, Math.ceil(filtered.length / userMappingPageSize));
                              setUserMappingPage(p => Math.min(totalPages, p + 1));
                            }}
                            disabled={(() => {
                              const filtered = userMappings.filter(mapping => {
                                const q = userMappingSearch.trim().toLowerCase();
                                if (!q) return true;
                                return (
                                  (mapping.firstName && mapping.firstName.toLowerCase().includes(q)) ||
                                  (mapping.lastName && mapping.lastName.toLowerCase().includes(q)) ||
                                  (mapping.email && mapping.email.toLowerCase().includes(q)) ||
                                  (mapping.standard && mapping.standard.toLowerCase().includes(q)) ||
                                  (mapping.sectors && mapping.sectors.toLowerCase().includes(q))
                                );
                              });
                              const totalPages = Math.max(1, Math.ceil(filtered.length / userMappingPageSize));
                              return userMappingPage === totalPages;
                            })()}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">
                    No users mapped to this qualification yet
                  </div>
                )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setMappingModalVisible(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setMappingModalVisible(false)}></div>
        </>
      )}

      <ConfirmationModal
        isOpen={Boolean(levelShiftConfirmation)}
        onClose={() => setLevelShiftConfirmation(null)}
        onConfirm={handleConfirmLevelShift}
        title="Konfirmasi Perubahan Qualification Level"
        message={
          levelShiftConfirmation ? (
            <div className="d-flex flex-column gap-3">
              <div>
                Qualification Level <strong>{levelShiftConfirmation.selectedLevel}</strong> sudah digunakan oleh{' '}
                <strong>"{levelShiftConfirmation.conflictLabel}"</strong>
                {levelShiftConfirmation.more} pada Qualification Group{' '}
                <strong>"{levelShiftConfirmation.selectedGroup}"</strong>.
              </div>
              {/* <div>
                Jika Anda klik <strong>OK</strong>, sistem akan otomatis menggeser level data yang bentrok pada group ini:{' '}
                <strong>
                  {levelShiftConfirmation.selectedLevel} -&gt; {levelShiftConfirmation.selectedLevel + 1},{' '}
                  {levelShiftConfirmation.selectedLevel + 1} -&gt; {levelShiftConfirmation.selectedLevel + 2}, dan seterusnya.
                </strong>
              </div>
              <div>
                Setelah itu, data <strong>"{levelShiftConfirmation.selectedName}"</strong> akan disimpan pada level{' '}
                <strong>{levelShiftConfirmation.selectedLevel}</strong>.
              </div>
              <div className="text-warning small">
                <strong>Perhatian:</strong> Jika ada data yang akan digeser sedang direferensikan oleh sistem lain (competency, impartiality, atau workflow), pergeseran tersebut akan gagal dan operasi akan dibatalkan. Dalam hal itu, silakan pilih Qualification Level yang tidak bentrok.
              </div> */}
            </div>
          ) : null
        }
        // confirmText="OK"
        cancelText="Cancel"
        type="warning"
        size="md"
        textAlign="start"
        isLoading={levelShiftLoading}
      />

      {/* Deactivate Qualification Modal */}
      {deactivateTarget && (
        <>
          <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deactivate Qualification</h5>
                  <button type="button" className="btn-close" onClick={closeDeactivateModal} disabled={deactivateLoading}></button>
                </div>
                <div className="modal-body">
                  <p className="text-secondary">
                    This will mark <strong>"{getDisplayQualificationName(deactivateTarget)}"</strong> ({getDisplayQualificationGroup(deactivateTarget)}) as{' '}
                    <strong>Inactive</strong>. It is not deleted and can be reactivated later.
                    {(deactivateTarget.userMappingCount || 0) > 0 && (
                      <> It is currently mapped to {deactivateTarget.userMappingCount} user(s).</>
                    )}
                  </p>
                  <label className="form-label">
                    Notes <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={deactivateNotes}
                    onChange={(e) => { setDeactivateNotes(e.target.value); setDeactivateError(''); }}
                    placeholder="Explain why this qualification is being deactivated"
                    disabled={deactivateLoading}
                    required
                  />
                  {deactivateError && <div className="text-danger small mt-2">{deactivateError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link" onClick={closeDeactivateModal} disabled={deactivateLoading}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-warning" onClick={handleConfirmDeactivate} disabled={deactivateLoading}>
                    {deactivateLoading ? 'Deactivating...' : 'Deactivate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={closeDeactivateModal}></div>
        </>
      )}

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default PersonnelQualification;
